/**
 * Kunga Basics Admin API Client
 * All requests go through the Fastify backend at /api/v1
 */

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

// ─── TOKEN MANAGEMENT ────────────────────────────────────────────────────────

let _token        = localStorage.getItem('kb_admin_token') ?? null;
let _refreshToken = localStorage.getItem('kb_admin_refresh') ?? null;
let _refreshing   = null; // single in-flight refresh promise

export function setToken(token, refreshToken) {
  _token = token;
  if (token) localStorage.setItem('kb_admin_token', token);
  else localStorage.removeItem('kb_admin_token');
  if (refreshToken !== undefined) {
    _refreshToken = refreshToken;
    if (refreshToken) localStorage.setItem('kb_admin_refresh', refreshToken);
    else localStorage.removeItem('kb_admin_refresh');
  }
}

export function getToken() { return _token; }

async function silentRefresh() {
  if (!_refreshToken) return false;
  if (_refreshing) return _refreshing;
  _refreshing = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Platform': 'admin' },
        body: JSON.stringify({ refreshToken: _refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (data.accessToken) { setToken(data.accessToken, data.refreshToken ?? _refreshToken); return true; }
      return false;
    } catch { return false; }
    finally { _refreshing = null; }
  })();
  return _refreshing;
}

// Proactively refresh every 12 minutes (access token is 15m)
setInterval(async () => {
  if (_token && _refreshToken) await silentRefresh();
}, 12 * 60 * 1000);

// ─── BASE FETCH ──────────────────────────────────────────────────────────────

async function request(method, path, body, opts = {}) {
  const hasBody = (method === 'POST' || method === 'PATCH') && !opts.rawBody;
  const headers = { ...(opts.headers ?? {}) };
  if (hasBody) headers['Content-Type'] = 'application/json';
  if (_token) headers['Authorization'] = `Bearer ${_token}`;
  headers['X-Client-Source'] = 'admin-portal';
  headers['X-Platform']     = 'admin';

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: opts.rawBody ? body : hasBody ? JSON.stringify(body ?? {}) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401 && _token) {
    // Try a silent refresh then replay the request once
    const ok = await silentRefresh();
    if (ok) return request(method, path, body, opts);
    setToken(null, null);
    window.dispatchEvent(new CustomEvent('session:expired'));
    return;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data?.error || `Request failed (${res.status})`), { status: res.status, data });
  return data;
}

const api = {
  get: (path, opts) => request('GET', path, null, opts),
  post: (path, body, opts) => request('POST', path, body, opts),
  patch: (path, body, opts) => request('PATCH', path, body, opts),
  delete: (path, opts) => request('DELETE', path, null, opts),
};

// ─── AUTH ────────────────────────────────────────────────────────────────────

export const auth = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  verifyMfa: (mfaToken, otp) => api.post('/auth/mfa/verify', { mfaToken, otp }),
  resendMfa: (mfaToken) => api.post('/auth/mfa/resend', { mfaToken }),
  sendMfaSetup: () => api.post('/auth/mfa/setup/send', {}),
  verifyMfaSetup: (otp) => api.post('/auth/mfa/setup/verify', { otp }),
  sendMfaDisable: () => api.post('/auth/mfa/disable/send', {}),
  verifyMfaDisable: (otp) => api.post('/auth/mfa/disable/verify', { otp }),
  me: () => api.get('/auth/me'),
  updateMe: (body) => api.patch('/auth/me', body),
  changePassword:  (body)             => api.post('/auth/change-password', body),
  verifyPassword:  (password)         => api.post('/auth/verify-password', { password }),
  resetPassword:   (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  logout: () => api.post('/auth/logout'),
  uploadAvatar: (file, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const fd = new FormData();
      fd.append('file', file, file.name);
      xhr.open('POST', `${BASE}/auth/avatar`);
      if (_token) xhr.setRequestHeader('Authorization', `Bearer ${_token}`);
      if (onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status < 300) resolve(data);
          else reject(Object.assign(new Error(data?.error ?? `Upload failed (${xhr.status})`), { status: xhr.status }));
        } catch { reject(new Error('Invalid response from server')); }
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(fd);
    });
  },
};

// ─── ADMIN DASHBOARD + ANALYTICS ─────────────────────────────────────────────

export const admin = {
  dashboard: () => api.get('/admin/dashboard'),
  activityLog: (page = 1) => api.get(`/admin/activity-log?page=${page}&limit=30`),
  moduleStats: () => api.get('/admin/module-stats'),
  paymentStats: () => api.get('/admin/payment-stats'),
  revenue: (from, to) => {
    const q = new URLSearchParams();
    if (from) q.set('from', from);
    if (to)   q.set('to', to);
    return api.get(`/admin/revenue${q.toString() ? '?' + q.toString() : ''}`);
  },
  signupTrend: () => api.get('/admin/signup-trend'),
  signinTrend: () => api.get('/admin/signin-trend'),
  notifications: () => api.get('/admin/notifications'),
  liveUsers: () => api.get('/admin/live-users'),
  // Pricing
  getPricing: () => api.get('/admin/pricing'),
  setPricing: (key, value) => api.patch('/admin/pricing', { key, value }),
  // SMTP test
  testEmail: (to) => api.post('/admin/test-email', { to }),
};

export const analytics = {
  overview: () => api.get('/analytics/overview'),
  funnel: () => api.get('/analytics/funnel'),
  retention: () => api.get('/analytics/retention'),
  mobileMoney: () => api.get('/analytics/mobile-money'),
};

export const books = {
  downloadStats: (page = 1, limit = 50) => api.get(`/books/stats?page=${page}&limit=${limit}`),
};

// ─── SITE / VISITOR ANALYTICS ────────────────────────────────────────────────

const qs = (params = {}) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, v); });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const siteAnalytics = {
  overview: (params)  => api.get(`/analytics/site/overview${qs(params)}`),
  geo:      (params)  => api.get(`/analytics/site/geo${qs(params)}`),
  devices:  (params)  => api.get(`/analytics/site/devices${qs(params)}`),
  pages:    (params)  => api.get(`/analytics/site/pages${qs(params)}`),
  sources:  (params)  => api.get(`/analytics/site/sources${qs(params)}`),
  trends:   (params)  => api.get(`/analytics/site/trends${qs(params)}`),
  realtime: (params)  => api.get(`/analytics/site/realtime${qs(params)}`),
  // Downloads the CSV export (auth header required) and saves it via the browser.
  exportCsv: async (params) => {
    const res = await fetch(`${BASE}/analytics/site/export${qs(params)}`, {
      headers: _token ? { Authorization: `Bearer ${_token}` } : {},
    });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visitor-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// ─── USERS ───────────────────────────────────────────────────────────────────

export const users = {
  list: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
    ).toString();
    return api.get(`/users${q ? `?${q}` : ''}`);
  },
  get: (id) => api.get(`/users/${id}`),
  sendEmail: (id, body) => api.post(`/users/${id}/send-email`, body),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`, {}),
  exportCSV: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
    ).toString();
    return fetch(`${BASE}/users/export/csv${q ? `?${q}` : ''}`, {
      headers: { Authorization: `Bearer ${_token}` },
    }).then(r => r.blob());
  },
};

// ─── MODULES ─────────────────────────────────────────────────────────────────

export const modules = {
  groups: () => api.get('/modules/groups/list'),
  list: (params = {}) => { const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))).toString(); return api.get(`/modules${q ? '?' + q : ''}`); },
  get: (id) => api.get(`/modules/${id}`),
  getDetails: (id) => api.get(`/modules/${id}/details`),
  create: (body) => api.post('/modules', body),
  update: (id, body) => api.patch(`/modules/${id}`, body),
  publish: (id) => api.post(`/modules/${id}/publish`, {}),
  unpublish: (id) => api.post(`/modules/${id}/unpublish`, {}),
  delete: (id) => api.delete(`/modules/${id}`),
  createGroup: (body) => api.post('/modules/groups', body),
  updateGroup: (id, body) => api.patch(`/modules/groups/${id}`, body),
  deleteGroup: (id) => api.delete(`/modules/groups/${id}`),
  archiveGroup: (id) => api.post(`/modules/groups/${id}/archive`, {}),
  unarchiveGroup: (id) => api.post(`/modules/groups/${id}/unarchive`, {}),
  listGroups: () => api.get('/modules/groups/list'),
  reorderGroups: (items) => api.patch('/modules/groups/reorder', { items }),
  reorderModules: (items) => api.patch('/modules/reorder', { items }),
  reorderVideos: (items) => api.patch('/modules/videos/reorder', { items }),
  reorderResources: (moduleId, items) => api.patch(`/modules/${moduleId}/resources/reorder`, { items }),
  permanentDeleteGroup: (id) => api.delete(`/modules/groups/${id}/permanent`),
  permanentDeleteModule: (id) => api.delete(`/modules/${id}/permanent`),
  // Resources
  listResources: (moduleId) => api.get(`/modules/${moduleId}/resources`),
  createResource: (moduleId, body) => api.post(`/modules/${moduleId}/resources`, body),
  updateResource: (id, body) => api.patch(`/modules/resources/${id}`, body),
  deleteResource: (id) => api.delete(`/modules/resources/${id}`),
  resourceUploadUrl: (filename, contentType) =>
    api.get(`/modules/resources/upload-url?filename=${encodeURIComponent(filename)}&contentType=${encodeURIComponent(contentType)}`),
  // Server-side upload: POST multipart/form-data, returns { fileUrl, key, filename, size }
  resourceUploadServer: (file, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const fd = new FormData();
      fd.append('file', file, file.name);
      xhr.open('POST', `${BASE}/modules/resources/upload-server`);
      if (_token) xhr.setRequestHeader('Authorization', `Bearer ${_token}`);
      if (onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status < 300) resolve(data);
          else reject(Object.assign(new Error(data?.error ?? `Upload failed (${xhr.status})`), { status: xhr.status }));
        } catch { reject(new Error('Invalid response from server')); }
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(fd);
    });
  },
};

// ─── VIDEOS ──────────────────────────────────────────────────────────────────

export const videos = {
  list: (params = {}) => { const q = new URLSearchParams(params).toString(); return api.get(`/videos${q ? `?${q}` : ''}`); },
  get: (id) => api.get(`/videos/${id}`),
  create: (body) => api.post('/videos', body),
  update: (id, body) => api.patch(`/videos/${id}`, body),
  delete: (id) => api.delete(`/videos/${id}`),
  permanentDelete: (id) => api.delete(`/videos/${id}/permanent`),
  uploadUrl: (body) => api.post('/videos/upload-url', body),
  streamUrl: (id) => api.get(`/videos/${id}/stream`),
};

// ─── SUBSCRIPTIONS ───────────────────────────────────────────────────────────

export const subscriptions = {
  list: (params = {}) => { const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))).toString(); return api.get(`/subscriptions${q ? '?' + q : ''}`); },
  getById: (userId) => api.get(`/subscriptions/${userId}/details`),
  override: (userId, body) => api.post(`/subscriptions/${userId}/override`, body),
  cancel: (userId) => api.post(`/subscriptions/${userId}/cancel`),
  restore: (userId) => api.post(`/subscriptions/${userId}/restore`),
};

// ─── DONATIONS ───────────────────────────────────────────────────────────────

export const donations = {
  list: (params = {}) => { const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null && v !== 0))).toString(); return api.get(`/donations${q ? '?' + q : ''}`); },
  stats: () => api.get('/donations/stats'),
  donorWall: () => api.get('/donations/donor-wall'),
  grantScholarship: (body) => api.post('/donations/scholarships/grant', body),
  scholarships: () => api.get('/donations/scholarships'),
};

// ─── ASK DR. GAD ─────────────────────────────────────────────────────────────

export const askGad = {
  queue: (params = {}) => {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))).toString();
    return api.get(`/ask-gad/admin/queue${q ? `?${q}` : ''}`);
  },
  getById: (id) => api.get(`/ask-gad/admin/${id}`),
  respond: (id, body) => api.post(`/ask-gad/admin/${id}/respond`, body),
  responseUploadUrl: () => api.get('/ask-gad/admin/response-upload-url'),
  assignees: () => api.get('/ask-gad/admin/assignees'),
  assign: (id, assignedToId) => api.post(`/ask-gad/admin/${id}/assign`, { assignedToId }),
  escalate: (id, reason) => api.post(`/ask-gad/admin/${id}/escalate`, { reason }),
};

// ─── ANNOUNCEMENTS ───────────────────────────────────────────────────────────

export const announcements = {
  list: () => api.get('/announcements/admin'),
  stats: () => api.get('/announcements/admin/stats'),
  viewers: (id) => api.get(`/announcements/admin/${id}/viewers`),
  create: (body) => api.post('/announcements/admin', body),
  update: (id, body) => api.patch(`/announcements/admin/${id}`, body),
  publish: (id) => api.post(`/announcements/admin/${id}/publish`, {}),
  delete: (id) => api.delete(`/announcements/admin/${id}`),
};

// ─── PAYMENTS / MOBILE MONEY ─────────────────────────────────────────────────

export const payments = {
  mobileMoneyList: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
    ).toString();
    return api.get(`/payments/mobile-money${q ? `?${q}` : ''}`);
  },
  manualActivate: (txId) => api.post(`/payments/mobile-money/${txId}/activate`),
  listTransactions: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
    ).toString();
    return api.get(`/payments/transactions${q ? `?${q}` : ''}`);
  },
  // Card payments — Stripe or Flutterwave; pass platform param to filter, omit for both
  stripeList: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
    ).toString();
    return api.get(`/subscriptions${q ? `?${q}` : ''}`);
  },
  /**
   * Upload a video directly to MinIO via a presigned PUT URL.
   * Steps:
   *   1. Ask the API for a presigned MinIO PUT URL (tiny request — no file bytes)
   *   2. PUT the file straight to MinIO from the browser (XHR progress works)
   *   3. Return { fileUrl } — the public URL of the uploaded video
   *
   * This avoids routing the file through the API server, eliminating the
   * timeout / memory-buffer issue that affected the old server-side path.
   */
  videoUploadServer: async (file, onProgress) => {
    // Step 1 — get presigned URL from API
    const { uploadUrl, fileUrl } = await api.get(
      `/videos/minio-upload-url?filename=${encodeURIComponent(file.name)}`
    );

    // Step 2 — PUT directly to MinIO
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
      }
      xhr.onload = () => {
        if (xhr.status < 300) resolve();
        else reject(new Error(`MinIO upload failed (${xhr.status}) — check MinIO CORS policy allows PUT from this origin.`));
      };
      xhr.onerror = () => reject(new Error('Network error uploading to MinIO — check CORS policy.'));
      xhr.send(file);
    });

    // Step 3 — return the public URL
    return { fileUrl };
  },
};

// ─── ROLES & PERMISSIONS ─────────────────────────────────────────────────────

export const roles = {
  list: () => api.get('/admin/roles'),
  create: (body) => api.post('/admin/roles', body),
  update: (id, body) => api.patch(`/admin/roles/${id}`, body),
  delete: (id) => api.delete(`/admin/roles/${id}`),
  getUsers: (id) => api.get(`/admin/roles/${id}/users`),
  updatePermissions: (id, permissionCodes) => api.patch(`/admin/roles/${id}/permissions`, { permissionCodes }),
};

export const permissions = {
  list: () => api.get('/admin/permissions'),
};

// ─── SUPPORT TEAM ────────────────────────────────────────────────────────────

export const supportTeam = {
  list: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
    ).toString();
    return api.get(`/admin/support-team${q ? `?${q}` : ''}`);
  },
  create: (body) => api.post('/admin/support-team', body),
  update: (id, body) => api.patch(`/admin/support-team/${id}`, body),
  activate: (id) => api.post(`/admin/support-team/${id}/activate`, {}),
  deactivate: (id) => api.post(`/admin/support-team/${id}/deactivate`, {}),
  resetPassword: (id) => api.post(`/admin/support-team/${id}/reset-password`, {}),
  regeneratePassword: (id) => api.post(`/admin/support-team/${id}/regenerate-password`, {}),
  updateRoles: (id, roleIds) => api.patch(`/admin/support-team/${id}/roles`, { roleIds }),
  updatePermissions: (id, permissionCodes) => api.patch(`/admin/support-team/${id}/permissions`, { permissionCodes }),
};

// ─── AUDIT LOG ───────────────────────────────────────────────────────────────

export const auditLog = {
  list: (params = {}) => api.get(`/admin/audit-log${qs(params)}`),
};

export const assessments = {
  list:   (params = {}) => api.get(`/assessments/admin/all${qs(params)}`),
  getById: (id)          => api.get(`/assessments/admin/${id}`),
};

export const manualPayments = {
  list:         (params = {}) => api.get(`/manual-payments${qs(params)}`),
  getById:      (id)          => api.get(`/manual-payments/${id}`),
  approve:      (id, body)    => api.post(`/manual-payments/${id}/approve`, body ?? {}),
  reject:       (id, body)    => api.post(`/manual-payments/${id}/reject`, body),
  pendingCount: ()            => api.get('/manual-payments/pending-count'),
};

export const countries = {
  list:       (params = {}) => api.get(`/country/admin${qs(params)}`),
  update:     (id, body)    => api.patch(`/country/admin/${id}`, body),
  bulk:       (body)        => api.post('/country/admin/bulk', body),
  analytics:  ()            => api.get('/country/admin/analytics'),
  audit:      (id)          => api.get(`/country/admin/${id}/audit`),
};